import withRole from "../../Hooks/withRole";
import GenericModal from "../GenericModal";
import { EWidths } from "../../Utils/Enums";
import useCustomMemo from "../../Hooks/useCustomMemo";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Fab,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Add, Delete, ChevronLeft, ChevronRight, CalendarViewMonth, CalendarToday } from "@mui/icons-material";
import Calendar from "../Calendar/Calendar";
import { ICalendarEvent } from "../Calendar/CalendarUtils";
import styles from "./ExamsCalendar.module.css";
import genericStyles from "../../Styles/genericStyles.module.css";
import { useExamsCalendar } from "./ExamsCalendarLogic";
import CertificateExamForm from "./CertificateExamForm";
import YearCalendar from "./YearCalendar";
import { FormsIds } from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useState } from "react";

function ExamsCalendar() {
  const memo = useCustomMemo();
  const workPoints = memo?.storage?.workPoints || [];
  const [view, setView] = useState<'month' | 'year'>('month');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [dayEventsOpen, setDayEventsOpen] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState<ICalendarEvent[]>([]);

  const {
    currentDate,
    setCurrentDate,
    events,
    openModal,
    setOpenModal,
    selectedEvent,
    setSelectedEvent,
    refreshEvents,
    selectedDate,
    setSelectedDate,
    selectedWorkPointId,
    handleWorkPointChange,
    deleteExam,
  } = useExamsCalendar();

  // Convert exam events to calendar format
  const calendarEvents: ICalendarEvent[] = events.map((event) => ({
    id: event.id.toString(),
    date: new Date(event.start).toISOString().split("T")[0], // YYYY-MM-DD format
    title: event.text,
    examData: event, // Keep original exam data
  }));

  // Handle date selection from calendar
  const handleDateSelect = (date: Date, dayEvents: ICalendarEvent[]) => {
    setSelectedDate(date);

    // On mobile, always show day events modal first
    if (window.innerWidth <= 768) {
      setSelectedDayEvents(dayEvents);
      setDayEventsOpen(true);
      return;
    }

    // Desktop behavior
    if (dayEvents.length === 1) {
      // If only one event, open it directly
      const examEvent = dayEvents[0].examData;
      setSelectedEvent(examEvent);
      setOpenModal(true);
    } else if (dayEvents.length === 0) {
      // No events, create new
      setSelectedEvent(null);
      setOpenModal(true);
    } else if (dayEvents.length > 1) {
      // Multiple events, show day events modal
      setSelectedDayEvents(dayEvents);
      setDayEventsOpen(true);
    }
  };

  // Handle month change
  const handleMonthChange = (date: Date) => {
    setCurrentDate(date);
  };

  // Check if mobile viewport
  const isMobile = () => window.innerWidth <= 768;

  // Mobile-optimized day cell renderer - minimal design
  const renderMobileDayCell = (day: number | null, dayEvents: ICalendarEvent[], isSelected: boolean) => {
    if (!day) return null;

    const hasEvents = dayEvents.length > 0;
    const isToday = new Date().getDate() === day && 
                   new Date().getMonth() === currentDate.getMonth() && 
                   new Date().getFullYear() === currentDate.getFullYear();

    return (
      <Box 
        className={`${styles.mobileDayCell} ${hasEvents ? styles.hasEvents : ''} ${isSelected ? styles.selected : ''} ${isToday ? styles.today : ''}`}
      >
        <Typography 
          variant="body2" 
          className={`${styles.dayNumber} ${isToday ? styles.todayNumber : ''}`}
        >
          {day}
        </Typography>
        {hasEvents && (
          <Box className={styles.mobileEventBadge}>
            <Typography variant="caption" className={styles.eventsCount}>
              {dayEvents.length}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Desktop day cell renderer - detailed design
  const renderDesktopDayCell = (day: number | null, dayEvents: ICalendarEvent[], isSelected: boolean) => {
    if (!day) return null;

    const hasEvents = dayEvents.length > 0;
    const isToday = new Date().getDate() === day && 
                   new Date().getMonth() === currentDate.getMonth() && 
                   new Date().getFullYear() === currentDate.getFullYear();

    return (
      <Box 
        className={`${styles.modernDayCell} ${hasEvents ? styles.hasEvents : ''} ${isSelected ? styles.selected : ''} ${isToday ? styles.today : ''}`}
      >
        {/* Day number */}
        <Box className={styles.dayHeader}>
          <Typography 
            variant="body2" 
            className={`${styles.dayNumber} ${isToday ? styles.todayNumber : ''}`}
          >
            {day}
          </Typography>
          {hasEvents && (
            <Box className={styles.eventsBadge}>
              <Typography variant="caption" className={styles.eventsCount}>
                {dayEvents.length}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Events list */}
        {hasEvents && (
          <Box className={styles.eventsList}>
            {dayEvents.map((event) => (
              <Box key={event.id} className={styles.eventItem}>
                <Box className={styles.eventDot} />
                <Typography variant="caption" className={styles.eventText}>
                  {event.title}
                </Typography>
                <IconButton
                  size="small"
                  className={styles.deleteBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteExam(event.id);
                  }}
                  title={Vocabulary.delete}
                >
                  <Delete fontSize="inherit" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Dynamic day cell renderer based on screen size
  const renderDayCell = (day: number | null, dayEvents: ICalendarEvent[], isSelected: boolean) => {
    return isMobile() 
      ? renderMobileDayCell(day, dayEvents, isSelected)
      : renderDesktopDayCell(day, dayEvents, isSelected);
  };

  const handleDeleteExam = (examId: string) => {
    // Find the exam to delete and set it as selected
    const examToDelete = events.find(event => event.id.toString() === examId);
    if (examToDelete) {
      setSelectedEvent(examToDelete);
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDeleteExam = async () => {
    if (!selectedEvent) return;
    
    try {
      await deleteExam(selectedEvent.id.toString());
      setDeleteConfirmOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error("Failed to delete exam:", error);
    }
  };

  const cancelDeleteExam = () => {
    setDeleteConfirmOpen(false);
    setSelectedEvent(null);
  };

  const handleDayEventSelect = (event: ICalendarEvent) => {
    setSelectedEvent(event.examData);
    setDayEventsOpen(false);
    setOpenModal(true);
  };

  const handleCreateNewExam = () => {
    setSelectedEvent(null);
    setDayEventsOpen(false);
    setOpenModal(true);
  };

  const closeDayEventsModal = () => {
    setDayEventsOpen(false);
    setSelectedDayEvents([]);
  };

  return (
    <Box className={styles.modernContainer}>
      {/* Header */}
      <Card className={styles.headerCard}>
        <CardContent className={styles.headerContent}>
          <Typography variant="h3" className={styles.title}>
            {Vocabulary.examsCalendar}
          </Typography>

          <Box className={styles.controlsContainer}>
            {/* View Toggle */}
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, newView) => newView && setView(newView)}
              className={styles.viewToggle}
              size="small"
            >
              <ToggleButton value="month" aria-label="month view">
                <CalendarToday />
              </ToggleButton>
              <ToggleButton value="year" aria-label="year view">
                <CalendarViewMonth />
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Work Point Filter */}
            <FormControl size="medium" className={styles.workPointFilter}>
              <InputLabel id="workpoint-select-label">
                {Vocabulary.workPoint}
              </InputLabel>
              <Select
                labelId="workpoint-select-label"
                value={selectedWorkPointId}
                onChange={(e) => handleWorkPointChange(e.target.value as string)}
                label={Vocabulary.workPoint}
                className={styles.workPointSelect}
              >
                <MenuItem value="">
                  <em>{Vocabulary.all}</em>
                </MenuItem>
                {workPoints.map((wp: any) => (
                  <MenuItem key={wp._id} value={wp._id}>
                    {wp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Navigation */}
            <Box className={styles.navigationContainer}>
              <IconButton
                className={styles.navButton}
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(currentDate.getMonth() - 1);
                  setCurrentDate(newDate);
                }}
              >
                <ChevronLeft />
              </IconButton>

              <Typography variant="h6" className={styles.dateDisplay}>
                {currentDate.toLocaleDateString("ro-RO", {
                  month: "long",
                  year: "numeric",
                })}
              </Typography>

              <IconButton
                className={styles.navButton}
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(currentDate.getMonth() + 1);
                  setCurrentDate(newDate);
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Calendar Views */}
      {view === 'month' ? (
        <Calendar
          events={calendarEvents}
          onDateSelect={handleDateSelect}
          onMonthChange={handleMonthChange}
          initialDate={currentDate}
          renderDayCell={renderDayCell}
          className={styles.examCalendar}
          showNavigation={false} // We have our own navigation in header
          showMonthNavigation={false} // Don't show month navigation
          currentDate={currentDate} // External control of current date
          // Let CSS handle all dimensions
          cellHeight="auto"
          rowHeight="auto"
        />
      ) : (
        <YearCalendar
          events={events}
          currentDate={currentDate}
          onMonthSelect={(date: Date) => {
            setCurrentDate(date);
            setView('month');
          }}
          selectedWorkPointId={selectedWorkPointId}
        />
      )}

      {/* Floating Action Button */}
      <Fab
        className={styles.addFab}
        onClick={() => {
          setSelectedEvent(null);
          setOpenModal(true);
        }}
      >
        <Add />
      </Fab>

      {/* Modal */}
      <GenericModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={selectedEvent ? "Detalii Examen" : "Examen Nou"}
        maxWidth={EWidths.MD}
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              type="button"
              color="error"
              onClick={() => setOpenModal(false)}
              variant="outlined"
              fullWidth
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              fullWidth
              type="submit"
              form={FormsIds.certificateExamForm}
              variant="contained"
            >
              {Vocabulary.save}
            </Button>
          </div>
        }
      >
        <Box className={styles.modalContent}>
          <CertificateExamForm
            selectedEvent={selectedEvent}
            selectedDate={selectedDate || undefined}
            onSubmitSuccess={() => {
              refreshEvents();
              setOpenModal(false);
            }}
            selectedWorkPointId={selectedWorkPointId}
          />
        </Box>
      </GenericModal>

      {/* Delete Confirmation Modal */}
      <GenericModal
        open={deleteConfirmOpen}
        onClose={cancelDeleteExam}
        title={Vocabulary.confirmDeleteExam}
        maxWidth={EWidths.MD}
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              type="button"
              color="primary"
              onClick={cancelDeleteExam}
              variant="outlined"
              fullWidth
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              fullWidth
              type="button"
              variant="contained"
              color="error"
              onClick={confirmDeleteExam}
            >
              {Vocabulary.delete}
            </Button>
          </div>
        }
      >
        <Box className={styles.modalContent}>
          <Typography variant="body1" gutterBottom>
            {Vocabulary.confirmDeleteExamMessage}
          </Typography>
          
          {selectedEvent && (
            <Box className={styles.examDetails}>
              <Typography variant="h6" className={styles.examHeader}>
                {Vocabulary.examDetails}:
              </Typography>
              
              <Box className={styles.detailItem}>
                <Typography className={styles.detailLabel}>
                  {Vocabulary.examDate}:
                </Typography>
                <Typography className={styles.detailValue}>
                  {new Date(selectedEvent.start).toLocaleDateString('ro-RO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Box>
              
              {selectedEvent.workPointId && (
                <Box className={styles.detailItem}>
                  <Typography className={styles.detailLabel}>
                    {Vocabulary.workPoint}:
                  </Typography>
                  <Typography className={styles.detailValue}>
                    {(() => {
                      const workPointIdValue = selectedEvent.workPointId as any;
                      const wpId = typeof workPointIdValue === 'object' ? workPointIdValue._id : workPointIdValue;
                      const wp = workPoints.find((w: any) => w._id === wpId);
                      return wp?.name || (typeof workPointIdValue === 'object' ? workPointIdValue.name : workPointIdValue) || 'N/A';
                    })()}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </GenericModal>

      {/* Day Events Modal */}
      <GenericModal
        open={dayEventsOpen}
        onClose={closeDayEventsModal}
        title={selectedDate ? `Examene - ${selectedDate.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' })}` : 'Examene'}
        maxWidth={EWidths.SM}
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              type="button"
              color="primary"
              onClick={closeDayEventsModal}
              variant="outlined"
              fullWidth
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              fullWidth
              type="button"
              variant="contained"
              color="primary"
              onClick={handleCreateNewExam}
            >
              Examen nou
            </Button>
          </div>
        }
      >
        <Box className={styles.dayEventsContent}>
          {selectedDayEvents.length === 0 ? (
            <Typography variant="body2" className={styles.noEventsText}>
              Nu sunt examene programate în această zi.
            </Typography>
          ) : (
            <Box className={styles.dayEventsList}>
              {selectedDayEvents.map((event) => (
                <Box key={event.id} className={styles.dayEventItem}>
                  <Box className={styles.dayEventInfo}>
                    <Typography variant="subtitle2" className={styles.dayEventTitle}>
                      {event.title}
                    </Typography>
                    <Typography variant="body2" className={styles.dayEventTime}>
                      {new Date(event.examData.start).toLocaleTimeString('ro-RO', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                    {event.examData.workPointId && (
                      <Typography variant="caption" className={styles.dayEventWorkPoint}>
                        {(() => {
                          const workPointIdValue = event.examData.workPointId as any;
                          const wpId = typeof workPointIdValue === 'object' ? workPointIdValue._id : workPointIdValue;
                          const wp = workPoints.find((w: any) => w._id === wpId);
                          return wp?.name || (typeof workPointIdValue === 'object' ? workPointIdValue.name : '') || wpId || '';
                        })()}
                      </Typography>
                    )}
                  </Box>
                  <Box className={styles.dayEventActions}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleDayEventSelect(event)}
                      className={styles.dayEventButton}
                    >
                      Vezi
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteExam(event.id)}
                      className={styles.dayEventDeleteBtn}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </GenericModal>
    </Box>
  );
}

export default withRole(ExamsCalendar);
